import { CellRange, Column, ColumnApi, GridApi } from '@ag-grid-community/core'
import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'
import {
  allowUpdateBasedOnType,
  getCellDataTypeForSingleDialogs,
  getCellOptionsForSingleDialogs,
  isSingleValueCase,
  toISOFormatFromAnyValidDateString,
} from '../../util/common-utils'

@Injectable()
export class PasteService {
  grid!: GridApi
  columnApi!: ColumnApi

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  readonly pasteBinding = this.pasteHandler.bind(this)

  registerGrid(grid: GridApi, columnApi: ColumnApi) {
    this.grid = grid
    this.columnApi = columnApi
    this.document.addEventListener('paste', this.pasteBinding)
  }

  destroy() {
    this.document.removeEventListener('paste', this.pasteBinding)
  }
  pasteHandler(event: ClipboardEvent): boolean {
    const data =
      event.clipboardData
        ?.getData('text/plain')
        .replace(/^\s+|\s+$|\r/g, '')
        .replace(/\r\n/g, '\n') ?? ''
    const rowWiseData = data.split('\n')

    const ranges = this.grid.getCellRanges()
    if (!ranges || ranges.length == 0) {
      return false
    }
    if (ranges.length > 1 && !isSingleValueCase(rowWiseData)) {
      return false
    }

    if (isSingleValueCase(rowWiseData)) {
      this.singleValueMultiRangePaste(rowWiseData[0].split('\t')[0], ranges)
    } else {
      this.selectedRangePaste(rowWiseData, ranges[0])
    }
    return true
  }
  singleValueMultiRangePaste(valueToPaste: string, ranges: CellRange[]) {
    ranges.forEach(range => {
      const startRow = range.startRow?.rowIndex ?? 0
      const endRow = range.endRow?.rowIndex ?? 0
      for (let i = startRow; i <= endRow; i++) {
        const rowNodeEffected = this.grid.getDisplayedRowAtIndex(i)
        if (rowNodeEffected) {
          range.columns.forEach(column => {
            if (column.isCellEditable(rowNodeEffected)) {
              const data = this.pasteBasedOnType(
                valueToPaste,
                column,
                rowNodeEffected.data,
              )
              if (data) {
                rowNodeEffected.setDataValue(column, data)
              }
            }
          })
        }
      }
    })
  }
  selectedRangePaste(rowWiseData: string[], cellRange: CellRange) {
    const startRowIndex = cellRange.startRow?.rowIndex ?? 0
    const endRowIndex = cellRange.endRow?.rowIndex ?? 0
    const columns = cellRange.columns
    const columnsInData = rowWiseData[0].split('\t').length
    if (
      rowWiseData.length != endRowIndex - startRowIndex + 1 ||
      columnsInData != columns.length
    ) {
      return
    }
    for (let i = startRowIndex; i <= endRowIndex; i++) {
      const row = this.grid.getDisplayedRowAtIndex(i)
      if (!row) {
        break
      }
      const currentRowData = rowWiseData[i - startRowIndex]
      const allCellData = currentRowData.split('\t')
      cellRange.columns.forEach((currentColumn: Column, index: number) => {
        if (currentColumn.isCellEditable(row)) {
          const dataToPaste = this.pasteBasedOnType(
            allCellData[index],
            currentColumn,
            row.data,
          )
          if (dataToPaste) row.setDataValue(currentColumn, dataToPaste)
        }
      })
    }
  }
  /**
   * if data is of unwanted type for column else return the refined value of data as per type
   *
   * @param dataCurrent current data to be pasted
   * @param currentColumn current column
   * @returns undefined if data is of unwanted type for column else return the refined value of data as per type
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pasteBasedOnType(dataCurrent: string, currentColumn: Column, rowData: any) {
    const dataType = getCellDataTypeForSingleDialogs(currentColumn, rowData)
    const options = getCellOptionsForSingleDialogs(currentColumn, rowData)
    if (dataType == 'Number') {
      dataCurrent = dataCurrent.replace(/,/g, '')
    }
    if (dataType == 'Date') {
      dataCurrent = toISOFormatFromAnyValidDateString(dataCurrent)
    }
    if (rowData.id == 'cusip') {
      dataCurrent = dataCurrent.toUpperCase()
    }

    if (
      rowData.id == 'tranType' ||
      rowData.id == 'transactionType' ||
      rowData.id == 'category'
    ) {
      dataCurrent = this.toTitleCase(dataCurrent)
    }
    if (
      allowUpdateBasedOnType(
        dataCurrent,
        dataType ?? 'String',
        options
          ? options.bindValue
            ? options.items.map(
                (item: { [x: string]: unknown }) => item[options.bindValue],
              )
            : options.items
          : undefined,
      )
    ) {
      return dataCurrent
    }
    return undefined
  }

  toTitleCase(tranType: string) {
    return tranType.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    })
  }
}
